import jsPDF from "jspdf";
import formatNumber from "@/app/infrastructures/misc/common-library/FormatNumber";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import { FlagsFeatureShipmentC1 } from "@/feature-flags/flags-shipment-c1";
import { estimationBound } from "../../../module";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";

const formatPrice = (value: number, country: string) => {
  return formatByCountry(value, {
    type: "money",
    country: country?.toUpperCase(),
    prefix: country === "MY" ? "RM" : "Rp"
  });
};

export const shiftX = (x: number) => x - 2;

export const maskPhoneLast4 = (phone: any) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const last4 = digits.slice(-4);
  return last4 ? `****${last4}` : "";
};

const resolveFontStyle = (fontName: string, style: string) => {
  if (fontName === "Roboto Mono") {
    if (style === "bold") return "bold";
    if (style === "semibold") return "semibold";
    return "normal";
  }

  if (style === "extrabold") return "extrabold";
  if (style === "bold") return "bold";
  if (style === "semibold") return "semibold";
  return "medium";
};

export const setFontAuto = (doc: jsPDF, text: string, style: string) => {
  const hasDigit = /[0-9]/.test(text);
  const fontName = hasDigit ? "Roboto Mono" : "Inter";
  doc.setFont(fontName, resolveFontStyle(fontName, style));
};

export const createSolidBadge = (
  doc: jsPDF | any,
  text: string,
  x: number,
  y: number,
  fontSize = 8
) => {
  doc.setLineWidth(0.3);
  doc.setFillColor("#333333");
  doc.setDrawColor("#333333");
  const w = doc.getTextDimensions(text, { fontSize }).w + 1.5;
  const h = doc.getTextDimensions(text, { fontSize }).h + 2;
  doc.roundedRect(x, y, w, h, 0.5, 0.5, "FD");
  doc.setTextColor("#FFFFFF");
  doc.setFont("Inter", "extrabold");
  doc.setFontSize(fontSize);
  doc.text(text, x + 0.8, y + 3.3);
  doc.setTextColor("#333333");
  return { w, h };
};

export const computeTariff = (params: {
  bookingType: string;
  accountType: string;
  data: PrintDataEntitas;
}) => {
  let shipmentId = params.data.sttShipmentId ? params.data.sttShipmentId : "";
  let isPriceOfCOD = params.data.sttIsCod;
  let totalBiaya: any = 0;
  let totalBiayaKirim: any = 0;
  let lainLain: any = 0;
  let pajak: any = 0;
  let showPajak = true;
  let isEstimationBound = false;
  let returnFee: any = "-";
  let showReturnFee = false;

  const pajakValue =
    Number(params.data.sttBmTaxRate) +
    Number(params.data.sttPpnTaxRate) +
    Number(params.data.sttPphTaxRate);

  if (
    params.data.isReverseJourney &&
    (shipmentId.startsWith(
      FlagsFeatureShipmentC1.feature_shipment_c1.getValue()
    ) ||
      shipmentId.match(/^AP|^AS/gi))
  ) {
    shipmentId = params.data.sttReverseJourneyShipmentId;
    isPriceOfCOD = params.data.sttReverseJourneyCodHandling;
  }

  if (params.data.isReverseJourney) {
    shipmentId = params.data.sttReverseJourneyShipmentId;
  }

  isEstimationBound = estimationBound(
    shipmentId,
    params.bookingType,
    params.accountType,
    isPriceOfCOD
  );

  const isCA = shipmentId?.match(/^AP|^AS|^AR|^AG|^AD|^AP|^AO/gi);
  const cond = isCA ? params.data.tariffVisible : (params.data.tariffVisible || !isEstimationBound)

  if (cond) {
    totalBiaya = formatPrice(
      Number(params.data.totalTariff),
      params.data.sttBookedByCountry
    );
    const bKirimCalculation =
      Number(params.data.sttShippingSurchargeRateAfterDiscount) +
      Number(params.data.sttPublishRateAfterDiscount) +
      Number(params.data.sttOriginDistrictRateAfterDiscount) +
      Number(params.data.sttDestinationDistrictRateAfterDiscount);
    totalBiayaKirim = formatPrice(
      Number(bKirimCalculation),
      params.data.sttBookedByCountry
    );
    const lainLainCalculation = params.data.sttIsPromo
      ? Number(params.data.sttCommoditySurchargeRateAfterDiscount) +
        Number(params.data.sttHeavyweightSurchargeRateAfterDiscount) +
        Number(params.data.sttDocumentSurchargeRateAfterDiscount) +
        Number(params.data.sttInsuranceRate) +
        Number(params.data.sttCodFee) +
        Number(params.data.sttWoodpackingAfterRateDiscount)
      : Number(params.data.sttCommoditySurchargeRate) +
        Number(params.data.sttHeavyweightSurchargeRate) +
        Number(params.data.sttDocumentSurchargeRate) +
        Number(params.data.sttInsuranceRate) +
        Number(params.data.sttCodFee) +
        Number(params.data.sttWoodpackingRate);
    lainLain = formatPrice(lainLainCalculation, params.data.sttBookedByCountry);
    pajak = formatPrice(pajakValue, params.data.sttBookedByCountry);
    showPajak = pajakValue > 0;

    showReturnFee = params.data.sttIsCod === true;
    if (showReturnFee) {
      returnFee = formatPrice(
        Number(params.data.sttReturnFee),
        params.data.sttBookedByCountry
      );
    }
  } else {
    totalBiaya = "-";
    totalBiayaKirim = "-";
    lainLain = "-";
    if (pajakValue > 0) {
      pajak = "-";
    } else {
      showPajak = false;
    }
    showReturnFee = false;
  }

  if (params.data.sttIsCod) {
    totalBiaya = params.data.isInternational
      ? formatNumber(Number(params.data.sttCodAmount), {
          prefix: params.data.currencyCode,
          currency: params.data.currencyCode
        })
      : formatPrice(
          Number(params.data.sttCodAmount),
          params.data.sttBookedByCountry
        );
  }

  return {
    totalBiaya,
    totalBiayaKirim,
    lainLain,
    pajak,
    showPajak,
    returnFee,
    showReturnFee,
    isEstimationBound
  };
};

export const buildDestinationLine = (data: PrintDataEntitas) => {
  const parts = [
    data.sttDestinationDistrictName
    //data.sttDestinationCityName,
    //data.sttDestinationProvinceName
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  const dedup: string[] = [];
  parts.forEach((p) => {
    if (!dedup.includes(p)) dedup.push(p);
  });
  return dedup.join(", ").toUpperCase();
};
